/* screens/reports/ReportDetail.jsx — детальная страница отчёта (S8, §2.4).
   3 рендерера: foodcost-optimize / abc-analyze / inventory-analyze + fallback raw JSON.
   «Назад» — честный возврат (navigate(-1) при наличии истории, иначе → /reports; фикс №3,
   НЕ жёстко на аналитику). «Удалить» — ConfirmDialog → DELETE → /reports.
   Визуальный язык блоков согласован с канвасами S6 (детальная = expanded версия канваса):
   переиспользуются CanvasMetrics/PriorityTag/FcStatusTag/RiskTag/TrendCell + cv-* классы.
   ХЕНДОФФ → pages/reports/ReportDetail.jsx (роут /reports/:id; navigate(-1)/fallback). */

import React from 'react';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { EmptyState, ForbiddenState } from '../../components/patterns/States.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { IconButton } from '../../components/ui/Buttons.jsx';
import { Field, TextInput, Textarea } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { useBackNav } from '../../hooks/useBackNav.jsx';
import { EDITABLE_REPORT_SPEC, reportsReal, toResultPatch } from '../../lib/adapters/reports.js';
import { downloadReportDocx } from '../../lib/api/reports.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';
import { CanvasMetrics, FcStatusTag, PriorityTag, RiskTag, TrendCell } from '../workspace/canvas/CanvasViewForm.jsx';
import { InventoryAnalysisReport } from '../../components/reports/InventoryAnalysisReport.jsx';
import { AbcAnalysisReport } from '../../components/reports/AbcAnalysisReport.jsx';
import { FoodcostOptimizationReport } from '../../components/reports/FoodcostOptimizationReport.jsx';

  const { useParams, useNavigate } = RR;
  const { useState } = React;
  const { useFakeQuery, useBreakpoint } = hooks;

  const TYPE_LABEL = { reduce_norm: 'Снизить норму', substitute: 'Замена', rebuild_semiproduct: 'Пересобрать п/ф', change_technology: 'Изменить технологию', change_purchase: 'Сменить закупку', change_price: 'Изменить цену' };
  // статус диагноза инвентаризации (reports/schemas.py InventoryDiagnosis.status)
  const INV_STATUS = {
    confirmed: { tone: 'bad', label: 'подтверждено' },
    awaiting_data: { tone: 'warn', label: 'ждём данные' },
    to_recount: { tone: 'neutral', label: 'пересчёт' },
  };

  function Section({ title, hint, children }) {
    return (
      <section className="report-sec">
        {title && <div className="report-sec__head"><h2 className="t-h4 report-sec__title">{title}</h2>{hint && <span className="t-caption text-mute">{hint}</span>}</div>}
        {children}
      </section>
    );
  }

  /* ── Драйверы себестоимости (доли) ── */
  function Drivers({ list = [] }) {
    const max = Math.max(1, ...list.map((d) => d.share_pct));
    return (
      <div className="cv-drivers">
        {list.map((d, i) => (
          <div className="cv-driver" key={i}>
            <span className="cv-driver__name truncate">{d.ingredient_name}</span>
            <span className="cv-driver__bar"><span className="cv-driver__fill" style={{ width: (d.share_pct / max * 100) + '%' }} /></span>
            <span className="cv-driver__val num">{fmt.money(d.cost_rub)} · {fmt.percent(d.share_pct)}</span>
          </div>
        ))}
      </div>
    );
  }

  /* ── Рендерер: foodcost-optimize ── */
  function FoodcostReport({ r }) {
    const m = r.metrics || {};
    return (
      <>
        <CanvasMetrics items={[
          { label: 'Текущий FC', value: fmt.percent(m.current_fc_pct), tone: 'bad' },
          { label: 'Целевой FC', value: fmt.percent(m.target_fc_pct), tone: 'ok' },
          { label: 'Потенциал экономии', value: fmt.money(m.potential_savings_rub), tone: 'ok' },
        ]} />
        <Section title="Драйверы себестоимости"><Drivers list={r.cost_drivers} /></Section>
        <Section title="Меры оптимизации">
          <div className="cv-measures cv-measures--doc">
            {(r.measures || []).map((mz, i) => (
              <div className="cv-measure" key={i}>
                <div className="cv-measure__head">
                  <span className="cv-measure__type t-eyebrow">{TYPE_LABEL[mz.type] || mz.type}</span>
                  <RiskTag risk={mz.risk} />
                </div>
                <p className="cv-measure__desc">{mz.title}</p>
                <div className="cv-measure__delta">
                  <span className="cv-measure__pair">{mz.before} <Icon name="arrow-right" size={12} /> <span className="cv-measure__to">{mz.after}</span></span>
                  <span className="cv-measure__pair">Экономия: <span className="num cv-measure__to">−{fmt.money(mz.savings_rub)}</span>{mz.savings_pct != null ? <span className="text-mute"> · {fmt.percent(mz.savings_pct)}</span> : null}</span>
                </div>
                {mz.justification && <p className="cv-measure__notes text-mute">{mz.justification}</p>}
                {mz.compensation && <p className="cv-measure__notes text-mute">Компенсация: {mz.compensation}</p>}
              </div>
            ))}
          </div>
        </Section>
        {r.recommendations && r.recommendations.length > 0 && (
          <Section title="Рекомендованные наборы">
            <div className="cv-recs">
              {r.recommendations.map((rec, i) => (
                <div className={'cv-rec' + (rec.label === 'Компромисс' ? ' cv-rec--pick' : '')} key={i}>
                  <span className="cv-rec__label">{rec.label}</span>
                  <span className="cv-rec__save num">−{fmt.money(rec.total_savings_rub)}</span>
                  <span className="cv-rec__meas t-caption text-mute">{(rec.measures_ids || []).join(' + ')}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
        {r.verdict && <div className="cv-callout report-verdict"><Icon name="sparkles" size={18} /><p>{r.verdict}</p></div>}
      </>
    );
  }

  /* ── Рендерер: abc-analyze ── */
  function AbcReport({ r }) {
    const m = r.metrics || {};
    return (
      <>
        <CanvasMetrics items={[
          { label: 'P1 · срочно', value: m.p1_count ?? '—', tone: 'bad' },
          { label: 'P2 · проблема', value: m.p2_count ?? '—', tone: 'warn' },
          { label: 'P3 · внимание', value: m.p3_count ?? '—' },
          { label: 'Потенциал', value: m.potential_count ?? '—', tone: 'info' },
        ]} />
        <Section title="Приоритеты позиций" hint="FC% ниже = лучше">
          <div className="cv-table-wrap">
            <table className="cv-table">
              <thead><tr><th>Блюдо</th><th>Приоритет</th><th className="num">FC%</th><th>Статус FC</th><th>Комментарий</th></tr></thead>
              <tbody>
                {(r.items || []).map((it, i) => (
                  <tr key={i}>
                    <td data-label="Блюдо"><span className="cv-strong">{it.dish_name}</span> <span className="cv-abc num">{it.abc_code}</span></td>
                    <td data-label="Приоритет"><PriorityTag priority={it.priority} /></td>
                    <td className="num" data-label="FC%">{fmt.percent(it.fc_pct)}</td>
                    <td data-label="Статус FC"><FcStatusTag status={it.fc_status} /></td>
                    <td data-label="Комментарий" className="cv-comment"><span className="text-dim">{it.comment}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
        {r.insufficient_data && r.insufficient_data.length > 0 && (
          <Section title="Недостаточно данных">
            <ul className="cv-note-list">
              {r.insufficient_data.map((x, i) => (<li key={i}><Icon name="alert-triangle" size={13} /><span className="cv-strong">{x.dish_name}</span> — <span className="text-mute">{x.reason}</span></li>))}
            </ul>
          </Section>
        )}
      </>
    );
  }

  /* ── Рендерер: inventory-analyze ── */
  function DeltaTable({ rows = [] }) {
    return (
      <div className="cv-table-wrap">
        <table className="cv-table">
          <thead><tr><th>Ингредиент</th><th className="num">Δ, кол-во</th><th className="num">Δ, ₽</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td data-label="Ингредиент"><span className="cv-strong">{r.ingredient}</span></td>
                <td className="num" data-label="Δ, кол-во">{r.delta_units} {r.unit}</td>
                <td data-label="Δ, ₽"><TrendCell deltaRub={r.delta_rub} goodDirection="up" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  function InventoryReport({ r }) {
    const m = r.metrics || {};
    // Период/выручка опциональны — пустые плитки с «—» не показываем.
    return (
      <>
        <CanvasMetrics items={[
          ...(m.months_count != null ? [{ label: 'Период, мес.', value: m.months_count }] : []),
          ...(m.revenue_total != null ? [{ label: 'Выручка', value: fmt.money(m.revenue_total) }] : []),
          { label: 'Недостачи', value: fmt.money(m.shortage_total_rub), tone: 'bad' },
          { label: 'Излишки', value: fmt.money(m.surplus_total_rub), tone: 'info' },
        ]} />
        {r.groups && r.groups.length > 0 && (
          <Section title="Причины расхождений">
            <div className="cv-chips">
              {r.groups.map((g, i) => <span className="cv-causechip" key={i}>{g.cause} <span className="num">{g.count}</span></span>)}
            </div>
          </Section>
        )}
        <Section title={`ТОП недостач (${(r.top_shortages || []).length})`}><DeltaTable rows={r.top_shortages} /></Section>
        <Section title={`ТОП излишков (${(r.top_surpluses || []).length})`}><DeltaTable rows={r.top_surpluses} /></Section>
        <Section title="Диагностика" hint="статус «хуже» не зелёный">
          <div className="cv-table-wrap">
            <table className="cv-table">
              <thead><tr><th>Ингредиент</th><th>Статус</th><th className="num">Эффект</th><th>Рекомендация</th><th>Нужные данные</th></tr></thead>
              <tbody>
                {(r.diagnostics || []).map((dg, i) => {
                  const st = INV_STATUS[dg.status] || INV_STATUS.awaiting_data;
                  return (
                    <tr key={i}>
                      <td data-label="Ингредиент"><span className="cv-strong">{dg.ingredient}</span>
                        <span className="cv-rowchips">{(dg.criteria || []).map((c, j) => <Pill key={j} tone="neutral" size="sm" glyph={false}>{c}</Pill>)}</span>
                      </td>
                      <td data-label="Статус"><Pill tone={st.tone} size="sm">{st.label}</Pill></td>
                      <td data-label="Эффект"><TrendCell deltaRub={dg.effect_rub} goodDirection="up" /></td>
                      <td data-label="Рекомендация" className="cv-comment"><span className="text-dim">{dg.recommendation}</span></td>
                      <td data-label="Нужные данные" className="text-mute">{dg.data_needed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
        {r.action_plan && r.action_plan.length > 0 && (
          <Section title="План действий"><ol className="cv-plan report-plan">{r.action_plan.map((a, i) => <li key={i}>{a}</li>)}</ol></Section>
        )}
        {r.scenarios && (
          <Section title="Сценарии">
            <div className="cv-recs">
              {['conservative', 'target'].map((k) => { const s = r.scenarios[k]; if (!s) return null; return (
                <div className={'cv-rec' + (k === 'target' ? ' cv-rec--pick' : '')} key={k}>
                  <span className="cv-rec__label">{s.label}</span>
                  <span className="cv-rec__save num">недостача {fmt.money(s.shortage_after_rub)}</span>
                  <span className="cv-rec__meas t-caption text-mute">излишки {fmt.money(s.surplus_after_rub)} · откл. {fmt.percent(s.deviation_pct)}</span>
                </div>
              ); })}
            </div>
          </Section>
        )}
      </>
    );
  }

  function RawFallback({ r }) {
    return (
      <div className="report-raw">
        <div className="callout callout--warn"><Icon name="alert-triangle" size={16} /><div><strong>Не удалось отобразить.</strong> Схема отчёта не распознана — показаны исходные данные.</div></div>
        <pre className="report-raw__pre num">{JSON.stringify(r.result, null, 2)}</pre>
      </div>
    );
  }

  /* ── Редактор авторской прозы отчёта (spec-driven по EDITABLE_REPORT_SPEC) ──
     Правит СЫРОЙ result (схема-форма). Вычисляемые числа недоступны — только текст. */
  function ReportEditPanel({ type, draft, setDraft }) {
    const spec = EDITABLE_REPORT_SPEC[type];
    if (!spec) return null;
    const setScalar = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
    const setItem = (listKey, idx, fkey, val) => setDraft((d) => ({
      ...d,
      [listKey]: (d[listKey] || []).map((it, i) => (i === idx ? { ...it, [fkey]: val } : it)),
    }));
    const renderField = (f, value, onChange) => (
      <Field key={f.key} label={f.label}>
        {f.multiline
          ? <Textarea minRows={2} maxRows={8} value={value} onChange={(e) => onChange(e.target.value)} />
          : <TextInput value={value} onChange={(e) => onChange(e.target.value)} />}
      </Field>
    );
    return (
      <div className="report-edit stack stack-20">
        <div className="callout callout--info" role="note">
          <Icon name="info" size={16} />
          <div>Правится только текст (выводы, рекомендации, комментарии). Цифры и расчёты отчёта менять нельзя.</div>
        </div>
        {spec.scalars.map((s) => renderField(s, draft[s.key] || '', (v) => setScalar(s.key, v)))}
        {spec.lists.map((l) => (
          <Section title={l.label} key={l.key}>
            <div className="stack stack-12">
              {(draft[l.key] || []).map((item, idx) => (
                <div className="report-edit__item" key={idx}>
                  <div className="t-eyebrow text-mute report-edit__item-title">{(item && item[l.titleField]) || ('#' + (idx + 1))}</div>
                  {l.fields.map((f) => renderField(f, (item && item[f.key]) || '', (v) => setItem(l.key, idx, f.key, v)))}
                </div>
              ))}
              {(!draft[l.key] || draft[l.key].length === 0) && (
                <p className="t-caption text-mute">Нет строк для правки.</p>
              )}
            </div>
          </Section>
        ))}
      </div>
    );
  }

  function renderBody(report) {
    switch (report.type) {
      case 'foodcost':
        // v2 (детерминированный пайплайн по калькуляционным картам) — единый
        // рендерер с кликабельными тегами мер/риска; старые отчёты — прежний.
        return report.result && report.result.v2
          ? <FoodcostOptimizationReport r={report.result} />
          : <FoodcostReport r={report.result} />;
      case 'abc':
        // v2 (детерминированный пайплайн) — единый рендерер с кликабельными
        // тегами приоритетов/FC (общий с канвасом); старые отчёты — прежний.
        return report.result && report.result.v2
          ? <AbcAnalysisReport r={report.result} />
          : <AbcReport r={report.result} />;
      case 'inventory':
        // v2 (детерминированный пайплайн) — новый единый рендерер с
        // кликабельными тегами критериев; старые отчёты — прежний.
        return report.result && report.result.v2
          ? <InventoryAnalysisReport r={report.result} />
          : <InventoryReport r={report.result} />;
      default: return <RawFallback r={report} />;
    }
  }

  function ReportDetailScreen() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const confirm = useConfirm();
    const { isMobile } = useBreakpoint();
    const q = useFakeQuery('report-' + id, () => reportsReal.getReport(id), [id]);
    const report = q.data;
    // F40-результаты (result_editable=false) read-only: бэк-merge валидирует
    // legacy-схемой и f40-форму отвергнет — кнопку правки не показываем.
    const editable = !!(report && EDITABLE_REPORT_SPEC[report.type]) && report.result_editable !== false;

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // Скачивание — async (серверная сборка docx): спиннер на кнопке,
    // ошибка — тост (тот же паттерн, что Excel-выгрузка виджетов аналитики).
    const downloadDocx = async () => {
      if (!report || downloading) return;
      setDownloading(true);
      try {
        await downloadReportDocx(id, (report.title || 'report-' + id) + '.docx');
      } catch {
        toast.show('Не удалось скачать файл. Попробуйте ещё раз.', 'danger');
      } finally { setDownloading(false); }
    };
    const startEdit = () => {
      // правим СЫРОЙ result (схема-форма); deep-copy, чтобы Отмена не оставляла следов
      setDraft(JSON.parse(JSON.stringify((report && report.result_raw) || {})));
      setEditing(true);
    };
    const cancelEdit = () => { setEditing(false); setDraft(null); };
    const save = async () => {
      if (!report) return;
      setSaving(true);
      try {
        const patch = toResultPatch(report.type, draft);
        await reportsReal.updateReport(id, patch ? { result: patch } : {});
        toast.show('Отчёт обновлён', 'success');
        setEditing(false); setDraft(null);
        q.reload();
      } catch (e) {
        toast.show((e && (e.detail || e.message)) || 'Не удалось сохранить отчёт', 'danger');
      } finally { setSaving(false); }
    };

    const goBack = useBackNav('/reports');   // S13 §2.3 — надёжный fallback (deep-link не ломается)
    const del = async () => {
      const ok = await confirm({ title: 'Удалить отчёт?', body: 'Отчёт «' + (report.title) + '» будет удалён. Действие необратимо.', confirmLabel: 'Удалить', tone: 'danger' });
      if (!ok) return;
      try {
        await reportsReal.deleteReport(id);
        toast.show('Отчёт удалён', 'success');
        navigate('/reports');
      } catch (e) { toast.show('Не удалось удалить', 'danger'); }
    };

    const typeLabel = report ? report.type_label : 'Отчёт';
    let actions = null;
    if (report && editing) {
      actions = (
        <>
          <Button variant="ghost" onClick={cancelEdit} disabled={saving}>Отмена</Button>
          <Button variant="primary" iconLeft="check" loading={saving} onClick={save}>Сохранить</Button>
        </>
      );
    } else if (report) {
      actions = (
        <>
          {isMobile
            ? <IconButton icon={downloading ? 'spinner' : 'download'} aria-label="Скачать .docx" onClick={downloadDocx} disabled={downloading} />
            : <Button variant="ghost" iconLeft="download" loading={downloading} onClick={downloadDocx}>Скачать</Button>}
          {/* бэк отдаёт chat_id только если чат-источник жив и виден юзеру */}
          {report.chat_id != null && (isMobile
            ? <IconButton icon="chat" aria-label="Вернуться в чат" onClick={() => navigate('/chat/' + report.chat_id)} />
            : <Button variant="ghost" iconLeft="chat" onClick={() => navigate('/chat/' + report.chat_id)}>Вернуться в чат</Button>)}
          {editable && (isMobile
            ? <IconButton icon="edit" aria-label="Редактировать отчёт" onClick={startEdit} />
            : <Button variant="ghost" iconLeft="edit" onClick={startEdit}>Редактировать</Button>)}
          {isMobile
            ? <IconButton icon="trash" tone="danger" aria-label="Удалить отчёт" onClick={del} />
            : <Button variant="danger" iconLeft="trash" onClick={del}>Удалить</Button>}
        </>
      );
    }

    return (
      <div className="section-page">
        <PageHeader subtitle={typeLabel} title={(report && report.title) || 'Отчёт'} actions={actions} />
        <div className="section-body report-doc">
          <button type="button" className="report-back" onClick={goBack}><Icon name="arrow-left" size={15} /> Назад</button>

          {q.loading && (
            <div className="report-skel-doc">
              <span className="skeleton" style={{ width: '70%', height: 34 }} />
              <span className="skeleton" style={{ width: 220, height: 14 }} />
              <div className="report-metrics-skel">{[0, 1, 2].map((i) => <span className="skeleton" key={i} style={{ height: 64, borderRadius: 'var(--radius)' }} />)}</div>
              <span className="skeleton" style={{ width: '92%', height: 120, borderRadius: 'var(--radius)' }} />
            </div>
          )}

          {!q.loading && q.error && q.error.status === 403 && <ForbiddenState body="У вас нет доступа к этому отчёту. Обратитесь к администратору пространства." />}
          {!q.loading && q.error && q.error.status !== 403 && (
            <EmptyState serif={false} icon="search" title="Отчёт не найден"
              body="Возможно, он был удалён или ссылка устарела." action={<Button variant="primary" onClick={() => navigate('/reports')}>К отчётам</Button>} />
          )}

          {!q.loading && !q.error && report && (
            <>
              {/* h2: единственный h1 страницы — в PageHeader (семантика/a11y) */}
              <h2 className="report-cover-title">{report.title}</h2>
              <div className="report-meta">
                <span className="report-meta__item"><Icon name="calendar" size={14} /> <span className="num">{fmt.date(report.created_at)}</span></span>
                <span className="report-meta__item"><Icon name="user" size={14} /> {report.created_by_email}</span>
              </div>
              <div className="report-content stack stack-20">
                {editing
                  ? <ReportEditPanel type={report.type} draft={draft} setDraft={setDraft} />
                  : renderBody(report)}
              </div>
              {editing && (
                <div className="inv-form__actions">
                  <Button variant="ghost" onClick={cancelEdit} disabled={saving}>Отмена</Button>
                  <Button variant="primary" iconLeft="check" loading={saving} onClick={save}>Сохранить</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

export { ReportDetailScreen };
